import { FC } from "react";
// eslint-disable-next-line no-restricted-imports
import { Provider as ReduxStoreProvider } from "react-redux";

import { ManageSectionPagesModal } from "./ManageSectionPagesModal";

import { MOBILE_OVERLAY_ROUTE_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { showConfirmationModal } from "mds/components/Modal";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { GearSettingsIcon } from "mds/icons";
import { store, useAppSelector } from "store/index";
import {
  selectCurrentSectionIsLinked,
  selectHasAccessablePages,
  selectIsEditModeEnabled,
} from "store/selectors";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { useNavigate, useQueryParamsManager } from "utils/routing";

// `navigate` is captured from the main-app Router (the button's tree). The modal mounts
// its own `BrowserRouter` (see `Modal.tsx`), so `Link`/`useNavigate` inside the modal
// would only update that nested router and the main app would never re-render. Passing
// the parent navigate through lets the link click close the modal and navigate the
// outer app together.
const showManageSectionPagesModal = (navigate: ReturnType<typeof useNavigate>) =>
  showConfirmationModal({
    title: (
      <span className="flex items-center gap-2">
        <GearSettingsIcon className="text-blue" size="m" />
        {t("manage_section_pages_modal.title")}
      </span>
    ),
    hideCancelConfirmButtons: true,
    className: "!w-[90%] !min-w-[800px] !max-w-[1200px] !pb-4",
    children: (onClose) => (
      <ReduxStoreProvider store={store}>
        <ManageSectionPagesModal
          onClose={onClose}
          onPageClick={(href) => {
            onClose();
            // We need to do this outside the modal so that we use the correct router.
            navigate(href);
          }}
        />
      </ReduxStoreProvider>
    ),
  });

export const ManageSectionPagesButton: FC = () => {
  const navigate = useNavigate();
  const isMdOrLarger = useIsMdOrLarger();
  const { setQueryParams } = useQueryParamsManager();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const hasAssessablePages = useAppSelector(selectHasAccessablePages);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);

  if ((!isInLinkedSection && !isEditModeEnabled) || !hasAssessablePages) {
    return null;
  }

  const onClick = () => {
    if (isMdOrLarger) {
      showManageSectionPagesModal(navigate).catch(
        toastLocalizedOperationCatcher("manage_section_pages_modal_failure"),
      );
    } else {
      setQueryParams({ [MOBILE_OVERLAY_ROUTE_QUERY_PARAM]: "manage_pages" });
    }
  };

  return (
    <Button
      className="gap-1.5 !text-blue"
      kind="transparentSquareLegacy"
      size="xs"
      title={t("manage_section_pages_modal.title")}
      onClick={onClick}
    >
      <GearSettingsIcon size="s" />
      {t("manage_section_pages_modal.button")}
    </Button>
  );
};
